/**
 * DOCUMENT READING (Build 2b) — turning an uploaded file into the plain text
 * that Build 2a already knows how to extract facts from.
 *
 * WHERE THIS SITS. `workers/extract.ts` takes text and proposes candidate facts,
 * each carrying a verbatim quotation that is checked against that text BYTE FOR
 * BYTE before the candidate is allowed out. That veto is the product's single
 * promise made mechanical. This module sits in FRONT of it and answers one
 * question: what text did the owner's file actually contain?
 *
 * WHY THAT QUESTION IS DANGEROUS, AND WHY THIS FILE IS SHAPED AROUND IT.
 * The evidence veto checks quotations against whatever text it is handed. It has
 * no way to know whether that text came off the page or was invented on the way.
 * So if this module ever returns prose that the document does not contain, the
 * veto does not merely fail to catch it — it actively CERTIFIES it, because the
 * model's quote will match the invented prose exactly. A fabricated price would
 * reach the review queue wearing a document reference and a verbatim-looking
 * citation, and every downstream check would report green.
 *
 * That is the failure this module exists to prevent, and it is the reason for
 * both hard rules below.
 *
 * RULE 1 — IMAGES ARE REFUSED AS A CATEGORY. The Workers AI converter describes
 * image files using vision models. A description is not a transcription: it is
 * new text about a picture. Correct behaviour for a document-search product,
 * catastrophic for this one. No image type is accepted, in any wrapper.
 *
 * RULE 2 — A SCAN IS REFUSED, NOT PROCESSED. A scanned PDF is a picture of a
 * page with no text layer. Conversion yields either almost nothing or, worse,
 * something written about the page rather than off it. Neither is the owner's
 * document. The density gate below refuses the file and says so plainly, rather
 * than passing thin or invented text downstream where it would be indexed as
 * fact. Refusing is a first-class outcome here, not an error path.
 *
 * RULE 3 — PLAIN TEXT NEVER TOUCHES A MODEL. A .txt or .csv is decoded in this
 * Worker with a strict UTF-8 decoder. Routing it through an AI converter would
 * introduce a model into a path that needs none, spend a metered call to obtain
 * bytes we already hold, and create a chance of alteration where there is
 * currently none. The cheapest, most faithful path is also the safest one, so
 * there is no trade-off to weigh.
 *
 * WHAT THIS MODULE DOES NOT DO. It does not store the file. Nothing is written
 * to R2 or anywhere else; the bytes live for the duration of one request and are
 * gone. Provenance survives without them, because every approved fact already
 * carries a verbatim quotation from the source alongside the document's name.
 * Keeping customers' original paperwork would create a class of personal data at
 * rest — an employee certificate carries a named individual — with a retention
 * policy, deletion-on-exit and signed-URL discipline attached, in order to let
 * an owner re-read a document he has on his own computer. The surface is not
 * worth the feature. (If case-file assembly later needs attachments, that is its
 * own batch and its own decision.)
 *
 * STILL TRUE OF THIS MODULE, AND NOW A DECISION MADE PER POD. Kelvo build step 3
 * reversed the answer for Kelvo's private pods, where a site operative needs to
 * open the RAMS itself: a pod with documentIngest.keepOriginals has the upload
 * route store the file through workers/documents.ts, AFTER this module has read
 * it and only if it yields a fact. This module still stores nothing: reading and
 * keeping are separate acts in separate files, and every Foredesk pod keeps the
 * answer above unchanged.
 *
 * NO RUNTIME IMPORTS, NO WORKERS TYPES. Same rule, and the same reason, as
 * counters-format, decay-format and fact-scope: nothing here may pull a
 * `D1Database` or `Ai` type into the Pages tsc graph. The converter arrives as a
 * narrow structural interface, which also makes the whole module trivially
 * testable without a network or an account.
 */

/**
 * Hard bounds. Every one of these is a refusal the owner would rather have than
 * the thing it prevents.
 */
export const UPLOAD_LIMITS = {
  /**
   * Bytes accepted in one upload. Comfortably larger than any price list or
   * terms sheet a trade business has, and far below the request-body ceiling —
   * the point is to refuse a video someone renamed to .pdf before it is
   * decoded, not to ration legitimate documents.
   */
  maxBytes: 8_000_000,
  /**
   * Total characters of extracted body text below which the file is refused.
   * A document carrying fewer than this is either a scan, a cover sheet, or a
   * conversion that went wrong — and in every one of those cases there is
   * nothing to extract, so proceeding can only produce invented facts.
   */
  minTextChars: 200,
  /**
   * Characters per page below which a PDF is treated as a scan. Ordinary
   * business prose runs to thousands of characters a page; a page yielding
   * fewer than this has no usable text layer. Deliberately generous — the cost
   * of a false refusal is the owner pasting the text instead (Build 2a still
   * works, untouched), and the cost of a false accept is a fabricated fact.
   */
  minCharsPerPage: 100,
} as const

/**
 * WHY EACH FAILURE IS A NAMED CODE rather than a message. The endpoint maps a
 * code to a status, and the dashboard maps it to a sentence the owner can act
 * on. A bare string would force one of those two layers to match on prose,
 * which is how a rewording silently changes an HTTP status.
 */
export type DocumentReadFailure =
  /** Not a type we accept at all — including every image type. */
  | 'unsupported_type'
  /** Filename extension and declared content type disagree. */
  | 'type_mismatch'
  /** Content type is accepted, but the bytes are not that type. */
  | 'not_that_type'
  /** Above UPLOAD_LIMITS.maxBytes. */
  | 'too_large'
  /** Zero bytes. */
  | 'empty'
  /**
   * A text file that decoded cleanly but carries too little to read — a short
   * note, not a document. Separate from 'empty' because the owner has
   * something to do about it (paste the note in), and the screen can only
   * offer that if the code says which case this is.
   */
  | 'too_short'
  /** Converted, but the text is too thin to be a real text layer — a scan. */
  | 'unreadable_scan'
  /** The converter errored, or returned a shape we cannot read. */
  | 'conversion_failed'

export class DocumentReadError extends Error {
  readonly reason: DocumentReadFailure
  constructor(reason: DocumentReadFailure, message: string) {
    super(message)
    this.name = 'DocumentReadError'
    this.reason = reason
  }
}

/**
 * An accepted upload type.
 *
 * `magic` is the byte prefix the real format must start with. It exists because
 * a content type is a CLAIM MADE BY THE CALLER and an extension is a claim made
 * by whoever named the file — neither is evidence. Checking the first bytes is
 * the only one of the three that inspects the thing itself.
 *
 * `decodeAsText` marks the types read directly in this Worker rather than sent
 * to a model (Rule 3 above).
 */
interface AcceptedType {
  extension: string
  mime: string
  /**
   * Other content types real browsers send for this same format, accepted as
   * the same claim. Kept to types observed in the wild, never widened for
   * convenience: each one is a claim this module agrees to treat as honest.
   */
  mimeAliases?: readonly string[]
  magic: string | null
  decodeAsText: boolean
}

/**
 * THE ALLOW-LIST. Anything absent is refused, which is the correct default for
 * an input surface: a deny-list would silently admit every format added to the
 * converter after this file was written.
 *
 * NO IMAGE TYPES, EVER. Not jpeg, not png, not webp, not svg, not heic. The
 * converter supports them and would return fluent, plausible, entirely
 * model-authored text. See Rule 1.
 */
export const ACCEPTED_TYPES: readonly AcceptedType[] = [
  { extension: '.pdf', mime: 'application/pdf', magic: '%PDF-', decodeAsText: false },
  {
    extension: '.docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // Every Office Open XML file is a zip. PK\x03\x04 is the local file header.
    magic: 'PK\u0003\u0004',
    decodeAsText: false,
  },
  {
    extension: '.xlsx',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    magic: 'PK\u0003\u0004',
    decodeAsText: false,
  },
  {
    extension: '.csv',
    mime: 'text/csv',
    // Windows labels a .csv with Excel's type whenever Excel is installed,
    // which is most office PCs; older systems use the other three.
    mimeAliases: ['application/vnd.ms-excel', 'application/csv', 'text/x-csv', 'text/comma-separated-values'],
    magic: null,
    decodeAsText: true,
  },
  { extension: '.txt', mime: 'text/plain', magic: null, decodeAsText: true },
] as const

/** The Accept attribute for the file picker, derived so the two cannot drift. */
export const UPLOAD_ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.map((t) => t.extension).join(',')

/**
 * The slice of the Workers AI binding this module uses. Declaring only what we
 * call keeps the module free of the full `Ai` type surface (which would drag
 * Workers types into the Pages graph) and makes it mockable with an object
 * literal. Same pattern, same reasoning, as `EmbeddingRunner` in retrieval.ts.
 */
export interface MarkdownConverter {
  toMarkdown(
    files: { name: string; blob: Blob }[],
    options?: { conversionOptions?: { pdf?: { metadata?: boolean } } },
  ): Promise<{ name: string; mimeType: string; format: string; data: string }[]>
}

export interface ReadDocumentParams {
  /**
   * The file's REAL name, as it was on the owner's computer — used for the
   * extension check and handed to the converter. Never the owner's label for
   * the document: a label is his wording and carries no extension, which is
   * how renaming a document once refused its upload (28 Sep 2026).
   */
  name: string
  /** Content type as declared by the caller. A claim, checked against the bytes. */
  declaredType: string
  /** The raw file. */
  bytes: ArrayBuffer
  /** Workers AI. Not used for the text types — see Rule 3. */
  converter: MarkdownConverter
}

/** Which byte-to-character table read the file. Null for converted types. */
export type TextEncodingUsed = 'utf-8' | 'windows-1252' | null

export interface ReadDocumentResult {
  /** The extracted text, ready for extractFactsFromDocument. */
  text: string
  /** Characters extracted before any truncation by the caller. */
  chars: number
  /** Page count where the converter reported one; null for non-paginated types. */
  pages: number | null
  /** True when the text came out of this Worker rather than a model. */
  decodedLocally: boolean
  /** The encoding used, for provenance. Null when a model did the reading. */
  encoding: TextEncodingUsed
  /**
   * The CANONICAL type of the accepted format — ACCEPTED_TYPES' mime, never the
   * caller's header. A stored original (Kelvo step 3) is labelled with this and
   * served with this, so what goes out is what the magic-byte check let in.
   */
  mime: string
}

/** The longest document name the schema stores (documents.name CHECK). */
const MAX_STORED_NAME = 200

/**
 * The name a stored original goes by: the owner's LABEL, so his list reads in
 * his words, ending in the real file's extension, so a downloaded copy opens in
 * the right program. His label is kept whole when it already ends in that
 * extension. When adding it would pass the schema's 200 characters, the label
 * is shortened rather than the extension dropped — a copy that will not open is
 * worse than a name that is a few characters shorter.
 */
export function withFileExtension(label: string, fileName: string): string {
  const extension = extensionOf(fileName)
  if (extension === '') return label.slice(0, MAX_STORED_NAME)
  const hasIt = label.toLowerCase().endsWith(extension)
  if (hasIt && label.length <= MAX_STORED_NAME) return label
  // His own spelling of the extension is kept when he wrote one.
  const tail = hasIt ? label.slice(-extension.length) : extension
  const base = hasIt ? label.slice(0, -extension.length) : label
  return base.slice(0, MAX_STORED_NAME - tail.length).trimEnd() + tail
}

/** Lower-cased extension including the dot, or '' when the name carries none. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  if (dot <= 0 || dot === name.length - 1) return ''
  return name.slice(dot).toLowerCase()
}

/**
 * A declared type that says nothing about the file. Browsers send an empty
 * type when the operating system has no mapping for the extension, and some
 * upload paths send the generic binary type. Neither is a claim to check —
 * refusing on it would refuse an ordinary file for what the browser did not
 * know. With no claim made, the extension and the bytes decide, as they
 * already do: the magic check for PDF, Word and Excel, and the text decoder's
 * binary fence for .txt and .csv.
 */
const NO_CLAIM_TYPES: readonly string[] = ['', 'application/octet-stream']

/** True when a declared type (already normalised) is a claim this format accepts. */
function claimMatches(accepted: AcceptedType, mime: string): boolean {
  return mime === accepted.mime || (accepted.mimeAliases ?? []).includes(mime)
}

/**
 * Strip parameters and case from a content type: `text/CSV; charset=utf-8`
 * becomes `text/csv`. Browsers routinely append a charset, and a naive equality
 * check would refuse a perfectly ordinary upload.
 */
function normaliseMime(declared: string): string {
  const semi = declared.indexOf(';')
  return (semi === -1 ? declared : declared.slice(0, semi)).trim().toLowerCase()
}

/**
 * Read the first bytes as Latin-1 so any byte maps to exactly one character.
 * A UTF-8 decode would mangle PK\x03\x04 into replacement characters and the
 * comparison would fail for the wrong reason.
 */
function leadingBytesAsLatin1(bytes: ArrayBuffer, length: number): string {
  const view = new Uint8Array(bytes, 0, Math.min(length, bytes.byteLength))
  let out = ''
  for (const byte of view) out += String.fromCharCode(byte)
  return out
}

/**
 * COUNT PAGES AND MEASURE BODY TEXT.
 *
 * The converter's PDF output is a metadata block followed by `### Page N`
 * headings. Both are stripped before measuring, because a scanned document
 * still carries a full metadata block (producer, title, format version) — and
 * measuring density against text the SCANNER wrote would let every scan through
 * on the strength of its own file properties. The gate must see only what came
 * off the page.
 *
 * The metadata block is asked to be omitted at the call site as well. It is
 * stripped here regardless: the option is honoured by a service we do not
 * control, and a gate that only works when an upstream flag is respected is not
 * a gate. Belt over brace, deliberately.
 */
export function measureBody(markdown: string): { pages: number | null; bodyChars: number } {
  const pageMarkers = markdown.match(/^###\s+Page\s+\d+\s*$/gim)
  const pages = pageMarkers ? pageMarkers.length : null

  const body = markdown
    // The `## Metadata` block, up to the next heading of the same level or EOF.
    .replace(/^##\s+Metadata\s*$[\s\S]*?(?=^##\s|\z)/gim, '')
    // Page headings themselves.
    .replace(/^###\s+Page\s+\d+\s*$/gim, '')
    // The leading `# <filename>` title the converter emits.
    .replace(/^#\s+\S.*$/m, '')
    // Markdown list/heading punctuation contributes no meaning to a density test.
    .replace(/[#*_>`|-]/g, '')

  return { pages, bodyChars: body.replace(/\s+/g, ' ').trim().length }
}

/**
 * DECODE A TEXT FILE, WITHOUT EVER SUBSTITUTING A CHARACTER.
 *
 * Both decoders are FATAL. A lenient decoder replaces every byte it cannot
 * understand with U+FFFD and returns something that still looks like text —
 * silently altered content, which is the one thing this module must never
 * produce. Failing is the correct outcome; guessing is not.
 *
 * WHY WINDOWS-1252 IS TRIED SECOND, AND WHY THAT IS NOT A GUESS. A price list
 * typed into Notepad on Windows and saved as ANSI writes `£` as the single byte
 * 0xA3, which is not valid UTF-8. Refusing that file would be a false refusal on
 * one of the most ordinary documents a UK trade business owns — and the owner
 * would have no idea what "not readable as text" meant or what to do about it.
 * windows-1252 is a fixed, total, deterministic byte-to-character table: this is
 * a second exact reading, not a repair. Five bytes are undefined in it, and the
 * fatal flag rejects those too, so a genuinely binary file still fails.
 *
 * The order matters and is not interchangeable. UTF-8 first, because a valid
 * UTF-8 file decoded as windows-1252 would succeed and produce mojibake — a
 * plausible-looking corruption, the worst possible outcome here.
 *
 * The encoding used is REPORTED rather than swallowed, because "we read this as
 * a Windows text file" is a fact about provenance the caller may want to log.
 */
function decodeText(bytes: ArrayBuffer): { text: string; encoding: TextEncodingUsed } {
  try {
    return {
      text: new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes),
      encoding: 'utf-8',
    }
  } catch {
    // fall through
  }
  let legacy: string
  try {
    legacy = new TextDecoder('windows-1252', { fatal: true, ignoreBOM: false }).decode(bytes)
  } catch {
    throw new DocumentReadError('not_that_type', 'That file is not readable as text.')
  }

  /**
   * THE FENCE ON THE FALLBACK, and why it is needed at all.
   *
   * windows-1252 defines all but five byte values, so it decodes almost any
   * binary file "successfully" — a renamed archive or image comes back as a wall
   * of `ÿÿÿÿ`, which is a string, passes the length floor, and would go on to
   * cost a metered extraction call on noise. The fatal flag cannot catch this:
   * the bytes ARE valid in the table, they just are not text.
   *
   * The fence is deliberately narrow. It applies ONLY on this fallback path,
   * because the fallback exists for one specific thing: legacy Western European
   * text, which is by construction overwhelmingly ASCII with a scattering of
   * `£`, `’` and accented letters. A document in Greek, Polish or Chinese is
   * valid UTF-8 and was already returned above, so it never reaches this check
   * and cannot be false-refused by it.
   */
  const nonAscii = (legacy.match(/[^\u0000-\u007f]/g) ?? []).length
  if (legacy.length > 0 && nonAscii / legacy.length > 0.2) {
    throw new DocumentReadError('not_that_type', 'That file is not readable as text.')
  }
  return { text: legacy, encoding: 'windows-1252' }
}

/**
 * Turn an uploaded file into text, or refuse it.
 *
 * ORDER IS DELIBERATE and runs cheapest-and-most-certain first: size, then the
 * caller's two claims (extension, content type), then the bytes themselves, and
 * only then anything metered. A file that is going to be refused is refused
 * before it costs anything — which is also what keeps a malicious upload from
 * being a billing event.
 */
export async function readDocument(params: ReadDocumentParams): Promise<ReadDocumentResult> {
  const { name, declaredType, bytes, converter } = params

  if (bytes.byteLength === 0) {
    throw new DocumentReadError('empty', 'That file is empty.')
  }
  if (bytes.byteLength > UPLOAD_LIMITS.maxBytes) {
    throw new DocumentReadError(
      'too_large',
      `That file is larger than ${Math.floor(UPLOAD_LIMITS.maxBytes / 1_000_000)} MB.`,
    )
  }

  const extension = extensionOf(name)
  const byExtension = ACCEPTED_TYPES.find((t) => t.extension === extension)
  if (!byExtension) {
    throw new DocumentReadError(
      'unsupported_type',
      'We can read PDF, Word, Excel, CSV and plain text files.',
    )
  }

  const mime = normaliseMime(declaredType)
  if (!NO_CLAIM_TYPES.includes(mime) && !claimMatches(byExtension, mime)) {
    throw new DocumentReadError(
      'type_mismatch',
      'That file’s name and its contents do not agree.',
    )
  }

  if (byExtension.magic) {
    const leading = leadingBytesAsLatin1(bytes, byExtension.magic.length)
    if (leading !== byExtension.magic) {
      throw new DocumentReadError(
        'not_that_type',
        'That file is not the kind of file its name says it is.',
      )
    }
  }

  // ---- Rule 3: plain text is decoded here, never sent to a model. ----
  if (byExtension.decodeAsText) {
    const decoded = decodeText(bytes)
    const chars = decoded.text.replace(/\s+/g, ' ').trim().length
    if (chars < UPLOAD_LIMITS.minTextChars) {
      throw new DocumentReadError('too_short', 'There is not enough text in that file to read.')
    }
    return {
      text: decoded.text,
      chars,
      pages: null,
      decodedLocally: true,
      encoding: decoded.encoding,
      mime: byExtension.mime,
    }
  }

  // ---- Everything else goes to the converter. ----
  let converted: { data: string }[]
  try {
    converted = await converter.toMarkdown(
      [{ name, blob: new Blob([bytes], { type: byExtension.mime }) }],
      { conversionOptions: { pdf: { metadata: false } } },
    )
  } catch {
    // The provider's own error text is not shown to the owner: it is written for
    // an engineer and may name internals. The caller logs the throw; this is the
    // sentence the owner reads.
    throw new DocumentReadError('conversion_failed', 'We could not read that file.')
  }

  const markdown = converted?.[0]?.data
  if (typeof markdown !== 'string' || markdown.trim() === '') {
    throw new DocumentReadError('conversion_failed', 'We could not read that file.')
  }

  const { pages, bodyChars } = measureBody(markdown)

  // ---- Rule 2: the scan gate. ----
  if (bodyChars < UPLOAD_LIMITS.minTextChars) {
    throw new DocumentReadError(
      'unreadable_scan',
      'That looks like a scan or a photo of a document rather than a text one, so we cannot read it reliably. Paste the text in instead and we will use that.',
    )
  }
  if (pages !== null && bodyChars / pages < UPLOAD_LIMITS.minCharsPerPage) {
    throw new DocumentReadError(
      'unreadable_scan',
      'That looks like a scan or a photo of a document rather than a text one, so we cannot read it reliably. Paste the text in instead and we will use that.',
    )
  }

  return { text: markdown, chars: bodyChars, pages, decodedLocally: false, encoding: null, mime: byExtension.mime }
}
